<template>
    <header>
        <div class="d-flex align-items-center">
            <div class="mx-3" v-if="!leftSidebarStatus.get">
                <img src="/assets/img/logo/logo-icon.svg" alt="Rehberim Sensin" draggable="false">
            </div>
            <div class="flex-fill">
                <div class="d-flex justify-content-between align-items-center">
                    <section class="user-name">
                        <h3 class="fs-3 mb-0">Merhaba, <span class="name">{{ C.Str(user.fullName) }}</span><span class="wave-emoji">👋</span></h3>
                        <h6 class="fs-5 good-day">{{ greetingMessage }}</h6>
                    </section>
                    <section class="header-actions">
                        <template v-if="userType == Enums.USER_TYPE.TEACHER || userType == Enums.USER_TYPE.INSTRUCTOR || isBuyPackage">
                            <div v-if="hasLiveMeeting" class="header-live-meeting" :class="{ open: liveMeetingDropdownOpen }">
                                <button
                                    type="button"
                                    class="header-icon-btn header-live-meeting-btn"
                                    :class="{ 'is-active': hasLiveMeeting }"
                                    :title="hasMultipleLiveMeetings ? 'Aktif özel dersler' : 'Özel derse katıl'"
                                    :aria-expanded="hasMultipleLiveMeetings ? liveMeetingDropdownOpen : false"
                                    @click.stop="handleLiveMeetingClick">
                                    
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                        <rect x="3" y="5" width="14" height="14" rx="3" stroke="currentColor" stroke-width="1.8"/>
                                        <path d="M17 10L21 7.5V16.5L17 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M8.5 9L12.5 12L8.5 15V9Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <span v-if="hasMultipleLiveMeetings" class="live-meeting-count">{{ liveMeetingOptions.length }}</span>
                                </button>

                                <div v-if="hasMultipleLiveMeetings && liveMeetingDropdownOpen" class="live-meeting-dropdown" @click.stop>
                                    <div class="live-meeting-dropdown-head">
                                        <div>
                                            <strong>Aktif Özel Dersler</strong>
                                            <span>Katılmak istediğiniz görüşmeyi seçin.</span>
                                        </div>
                                        <span class="live-meeting-total">{{ liveMeetingOptions.length }}</span>
                                    </div>

                                    <div class="live-meeting-list">
                                        <button
                                            v-for="meeting in liveMeetingOptions"
                                            :key="meeting.id"
                                            type="button"
                                            class="live-meeting-item"
                                            @click="joinLiveMeeting(meeting)">
                                            <span class="live-meeting-item-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                    <rect x="3" y="5" width="14" height="14" rx="3" stroke="currentColor" stroke-width="1.8"/>
                                                    <path d="M17 10L21 7.5V16.5L17 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                                </svg>
                                            </span>
                                            <span class="live-meeting-item-copy">
                                                <strong>{{ meeting.fullName }}</strong>
                                                <span>{{ D.DateToString(meeting.dayDate) }} · {{ D.TimeToString(meeting.dayDate) }}</span>
                                            </span>
                                            <span class="live-meeting-join">
                                                Görüşmeye Katıl
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                    <path d="M5 12H19M13 6L19 12L13 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                                </svg>
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <router-link
                                :to="Links.Url(Links.User.Message)"
                                class="header-icon-btn"
                                v-if="user.userType == Enums.USER_TYPE.STUDENT || user.userType == Enums.USER_TYPE.TEACHER || user.userType == Enums.USER_TYPE.INSTRUCTOR">
                                <span class="header-badge" v-if="isNewMessage">
                                    {{ messageCount > 9 ? '+9' : messageCount }}
                                </span>

                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                    <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z"
                                        stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </router-link>
                        </template>

                        <router-link :to="Links.Url(Links.User.Notification)" class="header-icon-btn">
                            <span class="header-badge" v-if="isNewNotification">
                                {{ notificationCount > 9 ? '+9' : notificationCount }}
                            </span>

                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
                                    stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
                                    stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </router-link>
                    </section>
                </div>
            </div>
            <div class="user-info-wrapper">
                <div class="dropdown user-header-card">
                    <button
                        class="user-header-btn"
                        type="button"
                        id="dropdownMenu"
                        data-bs-toggle="dropdown"
                        aria-expanded="false" >
                        <div class="user-image">
                            <img :src="getUserProfileImage()" :alt="C.Str(user.fullName)" />
                        </div>

                        <div class="user-header-text">
                            <span class="user-full-name"  :data-gender="user.gender == true ? 'Kadın' : (user.gender == false ? 'Erkek' : '-')" >{{ C.Str(user.fullName) }}</span>
                            <span class="user-role"><slot name="rule"></slot></span>
                            <!--<span class="user-role">{{ user.gender == true ? "Kadın" : (user.gender == false ? "Erkek" : "Belirsiz") }}</span>-->
                        </div>

                        <svg class="user-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>

                    <ul class="dropdown-menu" aria-labelledby="dropdownMenu">
                        <li><router-link :to="Links.Url(Links.Dashboard)" class="dropdown-item"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></svg>Panelim</router-link></li>
                        <li><router-link :to="Links.Url(Links.User.Profile)" class="dropdown-item"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/></svg>Profilim</router-link></li>
                        <slot></slot>
                        <li><a href="javascript:void(0)" @click="logout" class="dropdown-item"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m16 17 5-5-5-5M21 12H9M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/></svg><span>Çıkış Yap</span></a></li>
                    </ul>
                </div>
            </div>
        </div>
    </header>
    <template v-if="isActiveMeeting">
        <AgoraMeeting v-model:state="openActiveMeeting" :detail="list.appointment.selected"/>
    </template>
</template>
<script>
    import { reactive, toRefs, computed, nextTick, onMounted, onBeforeUnmount } from "vue";
    import { useStore } from "vuex";
    import { useRouter } from "vue-router";

    import { Constants } from "@/helpers/constants";
    import { Enums } from "@/helpers/enums";
    import { Links } from "@/helpers/links";
    import { appData, C, D, J, Text } from "@/helpers/global";
    import { Web } from "@/helpers/web";

    import AgoraMeeting from "@/components/AgoraMeeting.vue"

    import { UserService } from "@/services/api/user";
    import { StoreUser, StoreAppointment, StoreMessage, StoreNotification } from "@/store/constants";

    import AvatarStudentDefault from "@/assets/img/avatar-student-default.png";
    import AvatarConsultantDefault from "@/assets/img/avatar-consultant-default.png";

    export default {
        components: {
            AgoraMeeting
        },
        mounted() { this.get(); },
        unmounted() { this.bus.leave(); },
        setup() {
            const router = useRouter();
            const store = useStore();
            const e = reactive({
                bus: appData.eventBus([Constants.Key.LeftSidebarStatus, Constants.Key.MeetingStatus]),
                leftSidebarStatus: {
                    get: computed(() => { return e.bus.get(Constants.Key.LeftSidebarStatus); }),
                },

                get() {
                    appData.bus.on(e.bus, (event) => {
                        if (event.key == Constants.Key.MeetingStatus) {
                            if (C.Bool(event.value)) {
                                if (e.hasMultipleLiveMeetings)
                                {
                                    e.liveMeetingDropdownOpen = true;
                                }
                                else if (e.liveMeetingOptions.length === 1)
                                {
                                    e.joinLiveMeeting(e.liveMeetingOptions[0]);
                                }
                                e.bus.set(Constants.Key.MeetingStatus, false);
                            }
                        }
                    });
                    
                    const loadAppointment = () => {
                        e.currentHour = D.ServerNow().getHours();
                        if (
                            e.userType == Enums.USER_TYPE.TEACHER
                            || e.userType == Enums.USER_TYPE.INSTRUCTOR
                            || e.isBuyPackage) {
                            e.list.appointment.load(() => { });
                        }
                    };
                    loadAppointment();
                    setInterval(() => { loadAppointment(); }, 60000);
                },

                getUserProfileImage() {
                    if (e.user.userType == Enums.USER_TYPE.STUDENT) {
                        return AvatarStudentDefault;
                    }

                    if (
                        e.user.userType == Enums.USER_TYPE.TEACHER ||
                        e.user.userType == Enums.USER_TYPE.INSTRUCTOR
                    ) {
                        return AvatarConsultantDefault;
                    }

                    return Web.Media(e.user.profile, Web.Url(appData.default.profile));
                },
                
                user: computed(() => { return store.getters[StoreUser.get]; }),
                userType: computed(() => {
                    return C.Int(e.user.userType);
                }),
                isBuyPackage: computed(() => {
                    return C.Bool(e.user.isBuyPackage);
                }),
                currentHour: D.ServerNow().getHours(),
                greetingMessage: computed(() => {
                    if (e.currentHour >= 5 && e.currentHour < 12) {
                        return 'Günaydın';
                    }
                    else if (e.currentHour >= 12 && e.currentHour < 18) {
                        return 'İyi Günler';
                    }
                    else {
                        return 'İyi Akşamlar';
                    }
                }),
                messageCount: computed(() => {
                    const isStudent = e.userType == Enums.USER_TYPE.STUDENT;
                    return C.Int(store.getters[StoreMessage.getCountUnReaded](isStudent));
                }),
                notificationCount: computed(() => {
                    return C.Int(store.getters[StoreNotification.getCountUnReaded]);
                }),
                liveMeetingDropdownOpen: false,
                liveMeetingOptions: computed(() => {
                    return e.list.appointment.getActives;
                }),
                hasLiveMeeting: computed(() => e.liveMeetingOptions.length > 0),
                hasMultipleLiveMeetings: computed(() => e.liveMeetingOptions.length > 1),
                handleLiveMeetingClick() {
                    if (e.hasMultipleLiveMeetings) {
                        e.liveMeetingDropdownOpen = !e.liveMeetingDropdownOpen;
                        return;
                    }
                    if (e.liveMeetingOptions.length === 1) {
                        e.joinLiveMeeting(e.liveMeetingOptions[0]);
                    }
                },
                joinLiveMeeting(meeting) {
                    e.liveMeetingDropdownOpen = false;

                    if (!meeting) {
                        return;
                    }

                    e.list.appointment.selected = meeting;

                    if (meeting.meetingServer == Enums.MEETING_SERVER.AGORA) {

                        if (!appData.isMobile) {
                            // yeni sekmede aç
                            const url = router.resolve({
                                name: "agoraMeeting",
                                params: { id: meeting.id }
                            }).href;
                            window.open(url, "_blank", "noopener,noreferrer");
                            return;
                        }
                        
                        nextTick(() => {
                            e.openActiveMeeting = true;
                        });
                        return;
                    }
                    if (!Text.IsEmpty(meeting.meetingLink)) {
                        window.open(meeting.meetingLink, "_blank", "noopener,noreferrer");
                    }
                },
                closeLiveMeetingDropdown() {
                    e.liveMeetingDropdownOpen = false;
                },
                openActiveMeeting: false,
                isActiveMeeting: computed(() => {
                    return e.list.appointment.selected != null;
                }),
                isNewMessage: computed(() => {
                    return e.messageCount > 0;
                }),
                isNewNotification: computed(() => {
                    return e.notificationCount > 0;
                }),
                logout() {
                    UserService.Logout(true);
                },
                list: {
                    appointment: {
                        selected: null,
                        load(callback) {
                            nextTick(() => {
                                Web.Area((wait) => {
                                    store.dispatch(StoreAppointment.loadList, {
                                        wait, callback: () => { callback(); },
                                        userType: e.userType
                                    });
                                }, appData.fn.main());
                            });
                        },
                        get: computed(() => {
                            var l = J.Clone(store.getters[StoreAppointment.getList]);
                            l.forEach(r => {
                                r.dayDate = C.Date(r.dayDate);
                            });
                            l.sort((a, b) => {
                                if (a.dayDate >= b.dayDate) { return -1; }
                                return 1;
                            });
                            return l;
                        }),
                        getActives: computed(() => {
                            const l = e.list.appointment.get;

                            const now = D.ServerNow();

                            if (e.user.userType == Enums.USER_TYPE.INSTRUCTOR) {
                                const items = l.filter(r => C.Int(r.meeting.id) > 0
                                    && (r.meeting.server == Enums.MEETING_SERVER.AGORA || !Text.IsEmpty(r.link))
                                    && r.meeting.meetingState == Enums.MEETING_STATE.PENDING
                                    && D.DateAdd(Enums.DATE.MINUTE, -5, C.Date(r.dayDate)) <= now
                                    && now <= D.AddHour(1, C.Date(r.dayDate))
                                );

                                items.forEach(x => {
                                    if (x.meeting) {
                                        x.meetingId = x.meeting.id;
                                        x.meetingServer = x.meeting.server;
                                        x.meetingCode = x.meeting.code;
                                        x.meetingLink = x.meeting.link;
                                        x.meetingState = x.meeting.meetingState;
                                    }
                                });

                                return items;
                            }

                            return l.filter(r => C.Int(r.meetingId) > 0
                                && (r.meetingServer == Enums.MEETING_SERVER.AGORA || !Text.IsEmpty(r.meetingLink))
                                && r.meetingState == Enums.MEETING_STATE.PENDING
                                && D.DateAdd(Enums.DATE.MINUTE, -5, C.Date(r.dayDate)) <= now
                                && now <= D.AddHour(1, C.Date(r.dayDate))
                            );
                        }),
                    }
                }
            });

            onMounted(() => {
                document.addEventListener("click", e.closeLiveMeetingDropdown);
            });

            onBeforeUnmount(() => {
                document.removeEventListener("click", e.closeLiveMeetingDropdown);
            });

            return { ...toRefs(e) };
        },
    };
</script>
<style scoped>
.header-live-meeting {
    position: relative;
    display: flex;
    align-items: center;
}

.header-live-meeting-btn {
    position: relative;
    color: #6f35df !important;
    background: #fff !important;
    border-color: #e4d7fb !important;
}

.header-live-meeting-btn:hover,
.header-live-meeting-btn.is-active {
    color: #6328e4 !important;
    background: #f7f2ff !important;
    border-color: #d9c5ff !important;
}


.live-meeting-count {
    position: absolute;
    top: -6px;
    right: -6px;
    display: grid;
    place-items: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    color: #fff;
    background: #6328e4;
    border: 2px solid #fff;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}

.live-meeting-dropdown {
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    z-index: 1055;
    width: 330px;
    overflow: hidden;
    background: #fff;
    border: 1px solid #e7def5;
    border-radius: 16px;
    box-shadow: 0 18px 45px rgba(49, 33, 79, .16);
}

.live-meeting-dropdown::before {
    position: absolute;
    top: -6px;
    right: 19px;
    width: 12px;
    height: 12px;
    content: "";
    background: #fff;
    border-top: 1px solid #e7def5;
    border-left: 1px solid #e7def5;
    transform: rotate(45deg);
}

.live-meeting-dropdown-head {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 15px;
    background: linear-gradient(135deg, #fbf9ff 0%, #f5efff 100%);
    border-bottom: 1px solid #eee7f8;
}

.live-meeting-dropdown-head > div {
    display: flex;
    flex-direction: column;
    gap: 0px;
}

.live-meeting-dropdown-head strong {
    color: #282331;
    font-size: 14px;
    font-weight: 700;
}

.live-meeting-dropdown-head span {
    color: #8d8499;
    font-size: 11px;
}

.live-meeting-total {
    display: grid;
    flex: 0 0 29px;
    place-items: center;
    width: 29px;
    height: 29px;
    color: #6328e4 !important;
    background: #eee5ff;
    border-radius: 50%;
    font-size: 12px !important;
    font-weight: 800;
}

.live-meeting-list {
    padding: 0px;
}

.live-meeting-item {
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 11px 10px;
    color: inherit;
    text-align: left;
    background: transparent;
    border: 0;
    border-radius: 11px;
    transition: .18s ease;
}

.live-meeting-item + .live-meeting-item {
    border-top: 1px solid #f0ebf6;
    border-radius: 0 0 11px 11px;
}

.live-meeting-item:hover {
    background: #faf7ff;
}

.live-meeting-item-icon {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    color: #6328e4;
    background: #f1eaff;
    border: 1px solid #e3d5ff;
    border-radius: 11px;
}

.live-meeting-item-copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0px;
}

.live-meeting-item-copy strong {
    overflow: hidden;
    color: #2a2532;
    font-size: 12.5px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.live-meeting-item-copy span {
    overflow: hidden;
    color: #8a8194;
    font-size: 10.5px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.live-meeting-join {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #6328e4;
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
}

@keyframes liveMeetingPulse {
    0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, .28); }
    70% { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
    100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
}

@media (max-width: 767.98px) {
    .live-meeting-dropdown {
        position: fixed;
        top: 72px;
        right: 12px;
        left: 12px;
        width: auto;
        max-width: none;
    }

    .live-meeting-dropdown::before {
        display: none;
    }

    .live-meeting-item {
        grid-template-columns: 36px minmax(0, 1fr);
    }

    .live-meeting-join {
        grid-column: 2;
        justify-self: start;
    }
}
</style>
